// Utility functions for persisting checkbox states across page reloads

const STORAGE_KEY_PREFIX = 'jjt_checkbox_';

/**
 * Get the storage key for a specific component and checkbox
 * @param {string} component - Component identifier (e.g., 'admin_tutors', 'admin_tutees')
 * @param {string} checkboxName - Checkbox identifier (e.g., 'show_inactive')
 * @returns {string} Storage key
 */
const getStorageKey = (component, checkboxName) => `${STORAGE_KEY_PREFIX}${component}_${checkboxName}`;

/**
 * Save checkbox state to localStorage
 * @param {string} component - Component identifier
 * @param {string} checkboxName - Checkbox identifier
 * @param {boolean} checked - Checkbox state
 */
export const saveCheckboxState = (component, checkboxName, checked) => {
  try {
    const data = { checked, timestamp: Date.now() };
    localStorage.setItem(getStorageKey(component, checkboxName), JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save checkbox state to localStorage:', error);
  }
};

/**
 * Load checkbox state from localStorage
 * @param {string} component - Component identifier
 * @param {string} checkboxName - Checkbox identifier
 * @param {boolean} defaultValue - Default value if none saved
 * @returns {boolean} Checkbox state
 */
export const loadCheckboxState = (component, checkboxName, defaultValue = false) => {
  try {
    const stored = localStorage.getItem(getStorageKey(component, checkboxName));
    if (stored) {
      const data = JSON.parse(stored);
      
      // Check if the stored data is not too old (older than 90 days)
      const ninetyDaysAgo = Date.now() - (90 * 24 * 60 * 60 * 1000);
      if (data.timestamp && data.timestamp > ninetyDaysAgo) {
        // Validate the stored value is a boolean
        if (typeof data.checked === 'boolean') {
          return data.checked;
        }
      }
    }
  } catch (error) {
    console.warn('Failed to load checkbox state from localStorage:', error);
  }
  
  // Return default if no valid stored data
  return defaultValue;
};

/**
 * Clear stored checkbox state for a component and checkbox
 * @param {string} component - Component identifier
 * @param {string} checkboxName - Checkbox identifier
 */
export const clearCheckboxState = (component, checkboxName) => {
  try {
    localStorage.removeItem(getStorageKey(component, checkboxName));
  } catch (error) {
    console.warn('Failed to clear checkbox state from localStorage:', error);
  }
};

/**
 * Get all stored checkbox data (for debugging)
 * @returns {object} All stored checkbox data
 */
export const getAllStoredCheckboxStates = () => {
  const result = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        const identifier = key.replace(STORAGE_KEY_PREFIX, '');
        const data = JSON.parse(localStorage.getItem(key));
        result[identifier] = data;
      }
    }
  } catch (error) {
    console.warn('Failed to get all stored checkbox data:', error);
  }
  return result;
};